import { SidebarItem } from '../types';
import { ItemIcon, itemIcons, isItemIcon } from './itemIcons';

// Only presentation references are persisted. Runtime items always come from the authorized catalog.
export type ItemTarget = { type: 'screen'; menuId: string } | { type: 'url'; url: string };
export type MenuNode = {
  id: string; label?: string; icon?: ItemIcon; hidden?: boolean; target?: ItemTarget; children?: MenuNode[];
};
// Compatibility with preferences exported before custom nodes were named items.
const legacyItemPrefix = 'group:';
const canonicalId = (id: string) => id.startsWith(legacyItemPrefix) ? `item:${id.slice(legacyItemPrefix.length)}` : id;

export function isSafeItemUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return value.length <= 2048 && ['http:', 'https:'].includes(url.protocol);
  } catch { return false; }
}

function parseTarget(value: unknown): ItemTarget | undefined {
  if (value === undefined) return undefined;
  const target = value as ItemTarget | null;
  if (target?.type === 'screen' && typeof target.menuId === 'string'
    && target.menuId.startsWith('menu:') && target.menuId.length <= 500) {
    return { type: 'screen', menuId: target.menuId };
  }
  if (target?.type === 'url' && typeof target.url === 'string' && isSafeItemUrl(target.url.trim())) {
    return { type: 'url', url: target.url.trim() };
  }
  throw new Error('invalidMenuLayout');
}
export type MenuLayout = { format: 'smartsell-menu'; version: 1; items: MenuNode[] };
export type MenuCatalog = Map<string, SidebarItem>;
export const MAX_DEPTH = 6;

export function visibleMenu(items: SidebarItem[]): SidebarItem[] {
  return items.filter(item => item.show).flatMap(item => {
    if (!item.sidebarSubItems) return [{ ...item }];
    const children = visibleMenu(item.sidebarSubItems);
    return children.length ? [{ ...item, sidebarSubItems: children }] : [];
  });
}

export function menuDefaults(items: SidebarItem[]) {
  const catalog: MenuCatalog = new Map();
  const visit = (list: SidebarItem[], parent = ''): MenuNode[] => list.map(item => {
    const id = item.path ? `menu:${item.path}` : `section:${parent}/${item.title}`;
    catalog.set(id, item);
    return { id, ...(item.sidebarSubItems ? { children: visit(item.sidebarSubItems, id) } : {}) };
  });
  return { items: visit(visibleMenu(items)), catalog };
}

export const isCustomItem = (node: MenuNode) => canonicalId(node.id).startsWith('item:');

export function parseLayout(value: unknown): MenuLayout {
  const data = value as Partial<MenuLayout> | null;
  if (!data || data.format !== 'smartsell-menu' || data.version !== 1 || !Array.isArray(data.items)) {
    throw new Error('invalidMenuLayout');
  }
  let count = 0;
  const ids = new Set<string>();
  const visit = (list: unknown[], depth: number): MenuNode[] => {
    if ((depth > MAX_DEPTH && list.length > 0) || list.length > 1000) throw new Error('invalidMenuLayout');
    return list.map(value => {
      const node = value as MenuNode | null;
      if (!node || typeof node.id !== 'string' || node.id.length > 500 || ids.has(canonicalId(node.id)) || ++count > 1000) {
        throw new Error('invalidMenuLayout');
      }
      ids.add(canonicalId(node.id));
      if (!/^(menu:|section:|item:)/.test(canonicalId(node.id))) throw new Error('invalidMenuLayout');
      const custom = isCustomItem(node);
      if (custom && (typeof node.label !== 'string' || !node.label.trim() || node.label.length > 80)) {
        throw new Error('invalidMenuLayout');
      }
      if (node.hidden !== undefined && typeof node.hidden !== 'boolean') throw new Error('invalidMenuLayout');
      if (custom && node.icon !== undefined && !isItemIcon(node.icon)) throw new Error('invalidMenuLayout');
      if (node.children !== undefined && !Array.isArray(node.children)) throw new Error('invalidMenuLayout');
      return {
        id: canonicalId(node.id),
        ...(node.hidden ? { hidden: true } : {}),
        ...(custom && node.icon ? { icon: node.icon } : {}),
        ...(custom && node.target !== undefined ? { target: parseTarget(node.target) } : {}),
        ...(custom ? { label: node.label!.trim(), children: visit(node.children || [], depth + 1) } :
          node.children ? { children: visit(node.children, depth + 1) } : {})
      };
    });
  };
  return { format: 'smartsell-menu', version: 1, items: visit(data.items, 1) };
}

export function reconcileLayout(saved: MenuNode[], defaults: MenuNode[], catalog: MenuCatalog): MenuNode[] {
  const used = new Set<string>();
  const clean = (nodes: MenuNode[]): MenuNode[] => nodes.flatMap(original => {
    const node = { ...original, id: canonicalId(original.id) };
    if (used.has(node.id)) return [];
    const source = catalog.get(node.id);
    // An inaccessible parent never grants access to its descendants.
    if (!source && !isCustomItem(node)) return clean(node.children || []);
    used.add(node.id);
    const presentation = { id: node.id, ...(node.hidden ? { hidden: true } : {}) };
    if (isCustomItem(node)) return [{
      ...presentation, label: node.label, icon: isItemIcon(node.icon) ? node.icon : 'folder',
      children: clean(node.children || []),
      target: node.target?.type === 'url'
        ? (isSafeItemUrl(node.target.url.trim()) ? { ...node.target, url: node.target.url.trim() } : node.target)
        : node.target?.type === 'screen' && catalog.get(node.target.menuId)?.path ? node.target : undefined
    }];
    return [{ ...presentation, ...(source?.sidebarSubItems ? { children: clean(node.children || []) } : {}) }];
  });
  const result = clean(saved);
  const find = (nodes: MenuNode[], id: string): MenuNode | undefined => {
    for (const node of nodes) {
      if (node.id === id) return node;
      const child = find(node.children || [], id);
      if (child) return child;
    }
    return undefined;
  };
  const atDepthLimit = (list: MenuNode[], id: string, depth = 1): boolean => list.some(node =>
    (node.id === id && depth >= MAX_DEPTH) || atDepthLimit(node.children || [], id, depth + 1)
  );
  // New permissions and newly introduced menus remain discoverable in their default location.
  const appendMissing = (nodes: MenuNode[], destination: MenuNode[]) => {
    nodes.forEach(node => {
      let existing = find(result, node.id);
      if (!used.has(node.id)) {
        existing = { id: node.id, ...(node.children ? { children: [] } : {}) };
        destination.push(existing);
        used.add(node.id);
      }
      if (node.children) {
        const target = existing && atDepthLimit(result, existing.id) ? result : existing?.children || destination;
        appendMissing(node.children, target);
      }
    });
  };
  appendMissing(defaults, result);
  return result;
}

export function renderLayout(nodes: MenuNode[], catalog: MenuCatalog): SidebarItem[] {
  return nodes.flatMap(node => {
    if (node.hidden) return [];
    const source = catalog.get(node.id);
    const children = node.children ? renderLayout(node.children, catalog) : undefined;
    if (isCustomItem(node)) {
      const target = node.target?.type === 'screen' ? catalog.get(node.target.menuId) : undefined;
      const hasChildren = !!children?.length;
      const url = node.target?.type === 'url' && isSafeItemUrl(node.target.url) ? node.target.url : undefined;
      if (!hasChildren && !target?.path && !url) return [];
      return [{
        title: node.label || '', customLabel: true, menuId: node.id, show: true,
        icon: itemIcons[isItemIcon(node.icon) ? node.icon : 'folder'],
        sidebarSubItems: hasChildren ? children : undefined,
        path: hasChildren ? undefined : target?.path,
        externalUrl: hasChildren ? undefined : url,
        disabled: hasChildren ? undefined : target?.disabled
      }];
    }
    if (children && !children.length) return [];
    return source ? [{ ...source, menuId: node.id, sidebarSubItems: children }] : [];
  });
}

export function moveNode(nodes: MenuNode[], id: string, parentId: string, index: number): MenuNode[] {
  let moved: MenuNode | undefined;
  const remove = (list: MenuNode[]): MenuNode[] => list.flatMap(node => {
    if (node.id === id) { moved = node; return []; }
    return [{ ...node, ...(node.children ? { children: remove(node.children) } : {}) }];
  });
  const result = remove(nodes);
  if (!moved) return nodes;
  let inserted = false;
  const insert = (list: MenuNode[], parent: string): void => {
    if (parent === parentId) {
      const visible = list.filter(node => !node.hidden);
      const before = visible[Math.max(0, index)];
      list.splice(before ? list.indexOf(before) : list.length, 0, moved!);
      inserted = true;
      return;
    }
    list.forEach(node => { if (node.children) insert(node.children, node.id); });
  };
  insert(result, '');
  const depth = (list: MenuNode[]): number => list.length ? Math.max(...list.map(n => 1 + depth(n.children || []))) : 0;
  return inserted && depth(result) <= MAX_DEPTH ? result : nodes;
}

export const layoutFile = (items: MenuNode[]): MenuLayout => ({ format: 'smartsell-menu', version: 1, items });
